import React, { useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router-dom";

import { ProfileTab } from "enums/ProfileTabs";

import { useSOWQuery } from "services/input-core/sows/get-sow-by-id";
import { useWorkItemsFromSOW } from "services/input-core/work-items/get-work-items-from-sow";
import { useGetSOWsByIdSowOperativeQuery } from "services/input-core/sows/get-sow-operative-by-id";

import GridContainer from "components/UI/Grid/GridContainer";
import GridItemTS from "components/UI/Grid/GridItemTS";
import { TabContent, TabSystem } from "components/UI/TabSystem";
import Header from "./components/Header";
import SOWsRelated from "./components/SOWsRelated/SOWsRelated";
import WorkersRelated from "./components/WorkersRelated/WorkersRelated";

import GeneralTab, { BuildDetailsSection } from "./tabs/GeneralTab/GeneralTab";
import ActivityItemsTab from "./tabs/ActivityItemsTab/ActivityItemsTab";

import {
  parseDateAndTime,
  tryRetrieveDateFromUrlParams,
} from "helpers/DateHelper";

import { useSubscription } from "store/auth/auth-store";
import { toast } from "react-toastify";
import { useActivityItemsFromSOWWithStacks } from "services/input-core/activity-items/get-activity-item-from-sow-adm";
import {
  Line,
  StyledParagraph,
  StyledParagraphValue,
} from "./tabs/GeneralTab/GeneralTab.styles";

import { useActivityItemContext } from "store/activityItem";
import { useSowContext } from "store/sow";

const SowProfile: React.FC = () => {
  const {
    isKuo,
    isBeltrame,
    isAcelen,
    isAngloAmericaBrazil,
  } = useSubscription();
  const { t } = useTranslation("sow-profile");
  const navigate = useNavigate();

  const { getSowProfile } = useSowContext();

  const [searchParams] = useSearchParams();
  const sowId = searchParams.get("id");
  const currentTab = searchParams.get("tabs");

  const {
    setActivityItems,
    setLoading: setLoadingActivity,
    setRefetching: setRefetchingActivity,
  } = useActivityItemContext();

  const { data, isLoading } = useSOWQuery(sowId, "operative", {
    enabled: !!sowId,
    onSuccess: (data: any) => {
      getSowProfile(data);
      if (data?.status === "Closed")
        toast.error(t("info-sow-inactive", { ns: "list-messages" }));
    },
    onError: (error: any) => {
      if (error?.response?.data?.error === "Invalid sow template: operative") {
        toast.error(
          t("danger-sow-template-operative", { ns: "list-messages" }),
        );
        navigate(`/app/sowsAdm/profile?tab=${currentTab}&id=${sowId}`);
        return;
      }

      if (error?.response?.data?.statusCode === 404) {
        navigate("*");
        toast.error(t("danger-sow-not-found", { ns: "list-messages" }));
        return;
      }

      if (error?.response?.data?.statusCode === 403) {
        navigate("/app/access-denied");
        toast.error(t("access-denied-message", { ns: "list-messages" }));
        return;
      }
    },
  });

  const [date, setDate] = React.useState<Date>(() =>
    tryRetrieveDateFromUrlParams(),
  );

  const {
    data: activityItemsQuery,
    isLoading: isActivityItemsLoadingQuery,
    isRefetching: isActivityItemsRefetchingQuery,
    isInitialLoading: isActivityItemsInitialLoading,
    refetch: refetchActivityItems,
  } = useActivityItemsFromSOWWithStacks(data?.sowID, date, {
    enabled: !isLoading,
  });

  const {
    data: workItems,
    isLoading: isWorkItemsLoading,
    refetch: refetchWorkItems,
  } = useWorkItemsFromSOW(data?.sowID, date, {
    enabled: (isBeltrame || isAngloAmericaBrazil) && !isLoading,
  });

  const {
    data: sows,
    isLoading: isLoadingSows,
  } = useGetSOWsByIdSowOperativeQuery(data?.sowID, {
    enabled: !isLoading && !!sowId && !isBeltrame,
  });

  useEffect(() => {
    setLoadingActivity(isActivityItemsLoadingQuery);
    setRefetchingActivity(isActivityItemsRefetchingQuery);
    if (activityItemsQuery) setActivityItems(activityItemsQuery);
  }, [
    activityItemsQuery,
    isActivityItemsLoadingQuery,
    isActivityItemsRefetchingQuery,
    setActivityItems,
    setLoadingActivity,
    setRefetchingActivity,
  ]);

  const sowBuildDetails: BuildDetailsSection[] = [
    {
      title: t("SOW Details", { ns: "profile" }),
      items: [
        {
          text: t("SOW ID", { ns: "profile" }),
          value: data?.sowID,
        },
        {
          text: t("SOW Name", { ns: "profile" }),
          value: data?.sowName,
        },
        {
          text: t("Sow Owner", { ns: "profile" }),
          value: data?.sowOwner,
        },
        {
          text: t("Sow Creator", { ns: "profile" }),
          value: data?.sowCreator,
        },
        {
          text: t("Supplier", { ns: "profile" }),
          value: data?.supplierName,
        },
        {
          text: t("Primary Contact Person", { ns: "profile" }),
          value: data?.supplier?.supplierPrimaryContactPerson,
        },
      ],
    },
    {
      title: t("SOW Description", { ns: "profile" }),
      items: [
        {
          text: t("Description", { ns: "profile" }),
          value: data?.description,
          html: true,
        },
      ],
    },
    {
      title: t("Secondary Details", { ns: "profile" }),
      items: [
        {
          text: t("Billable", { ns: "profile" }),
          value: t(data?.billable),
        },
        {
          text: t("Created At", { ns: "profile" }),
          value: parseDateAndTime(data?.createdAt),
        },
        {
          text: t("Updated At", { ns: "profile" }),
          value: parseDateAndTime(data?.updatedAt),
        },
      ],
    },
    {
      title: t("Custom Fields", { ns: "profile" }),
      items: [
        {
          text: t("Fields for Uverify", { ns: "profile" }),
          value: (
            <div>
              {data?.customFields?.map(
                (
                  field: {
                    name: string;
                    value: any;
                  },
                  index: number,
                ) => (
                  <Line key={index}>
                    <StyledParagraph>
                      {t(field?.name, { ns: "custom-fields" })}:
                    </StyledParagraph>
                    {typeof field.value === "object" ? (
                      <StyledParagraphValue>
                        {JSON.stringify(field.value)}
                      </StyledParagraphValue>
                    ) : (
                      <StyledParagraphValue>
                        {t(field.value, { ns: "profile" })}
                      </StyledParagraphValue>
                    )}
                  </Line>
                ),
              )}
            </div>
          ),
        },
      ],
    },
  ];

  const tabs = useMemo(() => {
    return [
      {
        id: ProfileTab.GENERAL,
        title: t("General", { ns: "profile" }),
      },
      ...(isBeltrame || isAcelen || isAngloAmericaBrazil
        ? [
            {
              id: ProfileTab.ACTIVITY_ITEMS,
              title: t("Activity Items", { ns: "profile" }),
            },
          ]
        : [
            {
              id: ProfileTab.SOWS_RELATED,
              title: t("Sows Related", { ns: "profile" }),
            },
          ]),
      {
        id: ProfileTab.WORKERS_RELATED,
        title: t("Workers Related", { ns: "profile" }),
      },
    ].filter((tab) => !(tab.id === ProfileTab.ACTIVITY_ITEMS && isKuo));
  }, [isKuo, isBeltrame, isAcelen, isAngloAmericaBrazil]);

  return (isKuo && !isBeltrame) ||
    (!isKuo && isBeltrame) ||
    (!isKuo && !isBeltrame) ? (
    <GridContainer data-cy="sow-container">
      <GridItemTS xs={12}>
        <Header
          data={{
            fieldglassId: data?.sowID,
            name: data?.sowName,
            status: data?.status,
            zIdentifier: data?.zId,
            startDate: data?.startDate,
            endDate: data?.endDate,
            supplierName: data?.supplierName,
            supplierCode: data?.supplierCode,
          }}
          isLoading={isLoading}
          type="sow"
          data-cy="sow-header"
        />
      </GridItemTS>

      <GridItemTS xs={12} style={{ marginTop: "1rem" }}>
        <TabSystem tabs={tabs} syncWithUrlParams>
          <TabContent value={ProfileTab.GENERAL}>
            <GeneralTab
              isLoading={isLoading}
              buildDetails={sowBuildDetails}
              data-cy="general-tab"
            />
          </TabContent>
          {!isKuo && (
            <TabContent value={ProfileTab.ACTIVITY_ITEMS}>
              <ActivityItemsTab
                fieldglassId={data?.sowID}
                entityDate={{ start: data?.startDate, end: data?.endDate }}
                date={date}
                setDate={setDate}
                isActivityItemsInitialLoading={isActivityItemsInitialLoading}
                refetchActivityItems={refetchActivityItems}
                workItems={workItems}
                isWorkItemsLoading={isWorkItemsLoading}
                refetchWorkItems={refetchWorkItems}
                profile={"SOW"}
                supplierCode={data?.supplierCode}
                data-cy="activity-items-tab"
              />
            </TabContent>
          )}
          {!isBeltrame && (
            <TabContent value={ProfileTab.SOWS_RELATED}>
              <SOWsRelated
                sows={sows?.sowAdms}
                isLoading={isLoadingSows}
                isSowAdm={false}
                data-cy="sows-related-tab"
              />
            </TabContent>
          )}
          <TabContent
            value={ProfileTab.WORKERS_RELATED}
            data-cy="workers-related-tab"
          >
            <WorkersRelated sowID={data?.sowID} />
          </TabContent>
        </TabSystem>
      </GridItemTS>
    </GridContainer>
  ) : null;
};

export default SowProfile;
